import { createFileRoute, Link, useNavigate, useRouter } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { Loader2, Sparkles, ShieldCheck, Palette, Calendar, MessageSquare, FileText, Mail, Lock } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";

export const Route = createFileRoute("/portal/")({
  ssr: false,
  head: () => ({
    meta: [
      { title: "Customer Portal — Michael's Painting" },
      { name: "robots", content: "noindex,nofollow" },
      { name: "description", content: "Sign in to your Michael's Painting project portal." },
    ],
  }),
  component: PortalLanding,
});

function PortalLanding() {
  const navigate = useNavigate();
  const router = useRouter();
  const [mode, setMode] = useState<"password" | "magic">("password");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [magicSent, setMagicSent] = useState(false);

  useEffect(() => {
    supabase.auth.getUser().then(({ data }) => {
      if (data.user) navigate({ to: "/portal/dashboard" });
    });
  }, [navigate]);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true); setError(null);
    try {
      if (mode === "magic") {
        const { error: e2 } = await supabase.auth.signInWithOtp({
          email,
          options: { emailRedirectTo: `${window.location.origin}/portal/dashboard` },
        });
        if (e2) throw e2;
        setMagicSent(true);
      } else {
        const { error: e2 } = await supabase.auth.signInWithPassword({ email, password });
        if (e2) throw e2;
        await router.invalidate();
        navigate({ to: "/portal/dashboard" });
      }
    } catch (e2) { setError((e2 as Error).message); }
    finally { setBusy(false); }
  };

  return (
    <section className="min-h-screen bg-[#030303] text-white overflow-hidden relative">
      <div className="pointer-events-none absolute -top-40 -left-40 size-[480px] rounded-full bg-amber-400/10 blur-[120px]" />
      <div className="pointer-events-none absolute -bottom-40 -right-40 size-[520px] rounded-full bg-amber-300/5 blur-[120px]" />

      <div className="max-w-6xl mx-auto px-6 py-8 lg:py-16 grid lg:grid-cols-2 gap-12 lg:gap-20 items-center relative">
        {/* Left — pitch */}
        <div>
          <Link to="/" className="text-xs text-zinc-500 hover:text-amber-300 tracking-wider uppercase">← Back to site</Link>
          <p className="mt-8 text-amber-300 text-[11px] tracking-[0.3em] uppercase font-semibold">Customer Portal</p>
          <h1 className="font-serif text-4xl lg:text-5xl text-white mt-3 leading-tight">Your project,<br/>at your fingertips.</h1>
          <p className="text-zinc-400 mt-5 leading-relaxed max-w-md">Track your timeline, view your proposal and colors, message your project manager, upload photos, and access every document — all in one secure place.</p>

          <ul className="mt-10 space-y-3 max-w-md">
            {[
              { Icon: ShieldCheck, label: "Proposal & contract documents" },
              { Icon: Palette, label: "Selected paint colors with photorealistic previews" },
              { Icon: Calendar, label: "Schedule, reminders & appointment confirmations" },
              { Icon: MessageSquare, label: "Direct messaging with your project manager" },
              { Icon: FileText, label: "Warranty, invoices & care instructions" },
            ].map(({ Icon, label }) => (
              <li key={label} className="flex items-center gap-3 text-sm text-zinc-300">
                <span className="size-8 rounded-full bg-amber-300/10 border border-amber-300/20 grid place-items-center">
                  <Icon className="size-4 text-amber-300" />
                </span>
                {label}
              </li>
            ))}
          </ul>
        </div>

        {/* Right — auth card */}
        <div className="relative">
          <div className="absolute -inset-2 bg-gradient-to-br from-amber-300/20 to-transparent rounded-3xl blur-xl opacity-50" />
          <div className="relative rounded-3xl border border-white/10 bg-white/[0.03] backdrop-blur-xl p-8 lg:p-10">
            <div className="flex items-center gap-2 mb-2">
              <Sparkles className="size-4 text-amber-300" />
              <span className="text-[10px] uppercase tracking-[0.25em] text-amber-300 font-semibold">Sign in</span>
            </div>
            <h2 className="font-serif text-2xl text-white">Welcome back</h2>
            <p className="text-sm text-zinc-500 mt-1">Use your email and password, or send a magic link.</p>

            <div className="mt-6 inline-flex p-1 rounded-full bg-white/5 border border-white/10 text-xs">
              {(["password", "magic"] as const).map((m) => (
                <button key={m} type="button" onClick={() => { setMode(m); setError(null); setMagicSent(false); }}
                  className={`px-4 py-1.5 rounded-full transition-all ${mode === m ? "bg-amber-300 text-zinc-950 font-semibold" : "text-zinc-400 hover:text-white"}`}>
                  {m === "password" ? "Password" : "Magic link"}
                </button>
              ))}
            </div>

            {magicSent ? (
              <div className="mt-6 rounded-xl border border-amber-300/30 bg-amber-300/5 p-5 text-sm text-amber-100">
                <Mail className="size-4 mb-2 text-amber-300" />
                Check <span className="font-semibold">{email}</span> for a sign-in link. It expires in 1 hour.
              </div>
            ) : (
              <form onSubmit={submit} className="mt-6 space-y-3">
                <label className="block">
                  <span className="text-[11px] uppercase tracking-wider text-zinc-500">Email</span>
                  <div className="relative mt-1">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-zinc-500" />
                    <input type="email" autoComplete="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                      placeholder="you@example.com"
                      className="w-full bg-black/40 border border-white/10 rounded-xl pl-10 pr-3 py-3 text-[16px] focus:border-amber-300/50 outline-none" />
                  </div>
                </label>
                {mode === "password" && (
                  <label className="block">
                    <span className="text-[11px] uppercase tracking-wider text-zinc-500">Password</span>
                    <div className="relative mt-1">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-zinc-500" />
                      <input type="password" autoComplete="current-password" required minLength={8} value={password} onChange={(e) => setPassword(e.target.value)}
                        placeholder="••••••••"
                        className="w-full bg-black/40 border border-white/10 rounded-xl pl-10 pr-3 py-3 text-[16px] focus:border-amber-300/50 outline-none" />
                    </div>
                  </label>
                )}
                {error && <p className="text-sm text-red-400">{error}</p>}
                <button type="submit" disabled={busy} className="w-full bg-amber-300 text-zinc-950 font-semibold py-3 rounded-full text-sm hover:bg-amber-200 disabled:opacity-40 inline-flex items-center justify-center gap-2 transition-all">
                  {busy && <Loader2 className="size-4 animate-spin" />}
                  {mode === "password" ? "Sign in" : "Send magic link"}
                </button>
              </form>
            )}

            <p className="text-[11px] text-zinc-500 mt-6 leading-relaxed">
              First time here? You should have received an invitation email. Open the link there to claim your project, or contact your project manager.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}